/**
 * M6.5-W3b — Foldertree-paneel (admin-only, alleen in Base-WP).
 *
 * Vervangt in Base de Explorer + DiagramTabs combinatie. Toont per
 * workspace de Base-WP-root met folders alfabetisch gesorteerd. Per
 * folder is een type_group (archimate / sharp / bmc / c4) gezet die
 * bepaalt welke type-palette-drops geaccepteerd worden.
 *
 * v1 (deze iteratie) levert:
 *  - Folder-CRUD: create-form (naam + type_group-radio), inline rename
 *    en delete-confirm. Vervangt FoldersSection in /settings.
 *  - Lijst van bestaande folders met name + type_group-badge.
 *
 * v2 (volgende iteratie, samen met Kladblok):
 *  - Concept-groepering per type ONDER folder.
 *  - Drop-target voor type-palette met folder_class-validatie.
 *  - Relaties als sublist onder concept.
 *  - Drag-tussen-folders.
 *  - Delete-acties op concepten en relaties.
 */
import type { BilingualText, Concept, Relation } from "@ae/domain";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ApiError,
  api,
  type FolderDTO,
  type FolderTypeGroup,
  type WorkPackageDTO,
} from "../../api/client";
import { getRegistry, resolveBilingual } from "../../registry";
import { useSession } from "../../state/session";
import { useUi } from "../../state/ui";
import { useWorkspaceData } from "../../state/workspace";
import { RelationGlyph } from "../explorer/RelationGlyph";
import { readDroppedTypeName } from "../palette/dragTypeName";
import { usePanelWidth } from "../usePanelWidth";
import { MoveOutOfFolderDialog } from "./MoveOutOfFolderDialog";

const TYPE_ID_MIME = "application/ae-type-id";
const FOLDER_CLASS_MIME = "application/ae-folder-class";
const DEFAULT_STEREOTYPES_MIME = "application/ae-default-stereotypes";
const DEFAULT_ATTRIBUTES_MIME = "application/ae-default-attributes";
/** Intra-Base move-tussen-folders payload: `{conceptIds, sourceFolderId,
 *  folderClass}` als JSON-string. Apart MIME zodat we niet conflicteren
 *  met cross-canvas-drags die `application/ae-existing-concept-id`
 *  gebruiken voor "drop op canvas". */
const FOLDER_MOVE_MIME = "application/ae-folder-move";

interface CreateForm {
  name: string;
  description: string;
  typeGroup: FolderTypeGroup;
}

const EMPTY_FORM: CreateForm = { name: "", description: "", typeGroup: "archimate" };

const TYPE_GROUP_OPTIONS: { value: FolderTypeGroup; label: string; color: string }[] = [
  { value: "archimate", label: "ArchiMate", color: "#94a3b8" },
  { value: "sharp", label: "Sharp", color: "#facc15" },
  { value: "bmc", label: "BMC", color: "#fb923c" },
  { value: "c4", label: "C4", color: "#60a5fa" },
];

function badgeColor(group: FolderTypeGroup): string {
  return TYPE_GROUP_OPTIONS.find((o) => o.value === group)?.color ?? "#94a3b8";
}

function ConceptRow({
  concept,
  lang,
  isEditing,
  editValue,
  isSelected,
  isExpanded,
  outgoing,
  incoming,
  conceptNameById,
  onToggleExpansion,
  onSelect,
  onOpenAttributePanel,
  onBeginRename,
  onChangeEditValue,
  onCommitRename,
  onCancelRename,
  onDelete,
  onDeleteRelation,
  onOpenRelationAttributePanel,
  onMoveOut,
  folderId,
  folderClass,
  dragConceptIds,
}: {
  concept: Concept;
  lang: string;
  isEditing: boolean;
  editValue: string;
  isSelected: boolean;
  isExpanded: boolean;
  outgoing: Relation[];
  incoming: Relation[];
  conceptNameById: Map<string, BilingualText>;
  onToggleExpansion: () => void;
  onSelect: (event: React.MouseEvent) => void;
  onOpenAttributePanel: () => void;
  onBeginRename: () => void;
  onChangeEditValue: (v: string) => void;
  onCommitRename: () => void;
  onCancelRename: () => void;
  onDelete: () => void;
  onDeleteRelation: (r: Relation) => void;
  /** Klik op een relatie-rij selecteert die relatie + opent het
   *  AttributePanel (analoog aan `onOpenAttributePanel` voor concepten). */
  onOpenRelationAttributePanel: (r: Relation) => void;
  onMoveOut: () => void;
  folderId: string;
  folderClass: FolderTypeGroup;
  /** Concept-ids die meeverhuizen bij drag. Parent levert dit aan als
   *  multi-selectie binnen deze folder, anders alleen [concept.id]. */
  dragConceptIds: readonly string[];
}) {
  const { t } = useTranslation();
  const displayName = resolveBilingual(concept.name, lang) || concept.id;
  const totalRels = outgoing.length + incoming.length;
  // Alleen kijken: geen slepen, hernoemen, verplaatsen of wissen.
  const readOnly = useSession((s) => s.readOnly);
  return (
    <li className="flex flex-col">
      {/* biome-ignore lint/a11y/noStaticElementInteractions: folder-concept-rij; interactief (klik + drag) maar nog niet keyboard-focusbaar — treeitem-roving-a11y volgt in de tree-a11y-ronde */}
      <div
        draggable={!isEditing && !readOnly}
        onDragStart={(e) => {
          if (isEditing) return;
          e.dataTransfer.setData("application/ae-existing-concept-id", concept.id);
          e.dataTransfer.setData(
            FOLDER_MOVE_MIME,
            JSON.stringify({
              conceptIds: [...dragConceptIds],
              sourceFolderId: folderId,
              folderClass,
            }),
          );
          e.dataTransfer.effectAllowed = "copyMove";
        }}
        className={`group flex items-center gap-1 rounded px-1 py-0.5 text-xs ${
          isSelected ? "bg-(--ld-active) text-(--ld-accent)" : "text-(--ld-text)"
        }`}
      >
        <button
          type="button"
          onClick={onToggleExpansion}
          className="inline-block w-5 shrink-0 text-(--ld-muted) text-base leading-none hover:text-(--ld-text) disabled:opacity-30"
          aria-expanded={isExpanded}
          aria-label={
            isExpanded ? t("foldertree.concept.collapseAria") : t("foldertree.concept.expandAria")
          }
          disabled={totalRels === 0}
          title={
            totalRels === 0
              ? t("foldertree.concept.noRelations")
              : isExpanded
                ? t("foldertree.concept.collapse")
                : t("foldertree.concept.expand")
          }
        >
          {totalRels === 0 ? "·" : isExpanded ? "▾" : "▸"}
        </button>
        {isEditing ? (
          <input
            type="text"
            value={editValue}
            onChange={(e) => onChangeEditValue(e.target.value)}
            onBlur={onCommitRename}
            onKeyDown={(e) => {
              if (e.key === "Enter") onCommitRename();
              if (e.key === "Escape") onCancelRename();
            }}
            // biome-ignore lint/a11y/noAutofocus: inline-rename triggered by explicit click
            autoFocus
            className="flex-1 rounded border border-(--ld-control) px-1 py-0 text-xs"
          />
        ) : (
          <button
            type="button"
            onClick={(e) => onSelect(e)}
            onDoubleClick={(e) => {
              // Dubbelklik forceert AP-open via onSelect (idempotent) + roept
              // expliciet de open-callback aan via een 0-modifier synthetic
              // event. Rename blijft beschikbaar via de ✎-knop hiernaast —
              // tegen het foldertree-default-gedrag is dubbelklik = detail
              // tonen, niet = renamen.
              onSelect(e);
              onOpenAttributePanel();
            }}
            className="flex-1 cursor-pointer truncate text-left hover:text-(--ld-text)"
            title={t("foldertree.concept.rowTitle", { typeId: concept.typeId })}
          >
            {displayName}
          </button>
        )}
        {/* hidden i.p.v. opacity-0 zodat de buttons geen flex-ruimte
            innemen wanneer ze onzichtbaar zijn (concept-naam truncate't
            anders te vroeg). */}
        <div
          className="hidden items-center gap-1 group-hover:flex focus-within:flex"
          hidden={readOnly}
        >
          <button
            type="button"
            onClick={onBeginRename}
            title={t("foldertree.concept.rename")}
            className="rounded px-1 text-(--ld-muted) hover:bg-(--ld-raised) hover:text-(--ld-text)"
          >
            ✎
          </button>
          <button
            type="button"
            onClick={onMoveOut}
            title={t("foldertree.multiSelect.moveOutTitle")}
            className="rounded px-1 text-(--ld-muted) hover:bg-(--ld-raised) hover:text-(--ld-text)"
          >
            📤
          </button>
          <button
            type="button"
            onClick={onDelete}
            title={t("settings.common.delete")}
            className="rounded px-1 text-(--ld-muted) hover:bg-(--ld-negative-bg) hover:text-(--ld-negative)"
          >
            🗑
          </button>
        </div>
      </div>
      {isExpanded && totalRels > 0 && (
        <ul className="ml-4 flex flex-col gap-0.5 border-(--ld-border) border-l pl-2 text-(--ld-muted) text-xs">
          {[
            ...outgoing.map((r) => ({ r, reversed: false })),
            ...incoming.map((r) => ({ r, reversed: true })),
          ].map(({ r, reversed }) => {
            const otherId = reversed ? r.sourceConceptId : r.targetConceptId;
            const otherName = resolveBilingual(conceptNameById.get(otherId) ?? {}, lang) || otherId;
            const reg = getRegistry();
            const rt = reg.getRelationType(r.typeId);
            const typeLabel = rt ? resolveBilingual(rt.label, lang) : r.typeId;
            return (
              <li
                key={`rel:${r.id}:${reversed ? "in" : "out"}`}
                className="group flex items-center gap-2 px-1 py-0.5 hover:bg-(--ld-raised)"
                title={t("foldertree.relation.rowTitle", { typeId: r.typeId, typeLabel })}
              >
                <button
                  type="button"
                  onClick={() => onOpenRelationAttributePanel(r)}
                  onDoubleClick={() => onOpenRelationAttributePanel(r)}
                  className="flex flex-1 items-center gap-2 text-left"
                >
                  <span className="w-20 shrink-0 truncate text-(--ld-text)">{typeLabel}</span>
                  <RelationGlyph typeId={r.typeId} attributes={r.attributes} reversed={reversed} />
                  <span className="truncate text-(--ld-text)">{otherName}</span>
                </button>
                <button
                  type="button"
                  onClick={() => onDeleteRelation(r)}
                  hidden={readOnly}
                  title={t("foldertree.relation.deleteTitle")}
                  className="ml-auto rounded px-1 text-(--ld-muted) opacity-0 hover:bg-(--ld-negative-bg) hover:text-(--ld-negative) group-hover:opacity-100"
                >
                  🗑
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </li>
  );
}

function typeGroupLabel(group: FolderTypeGroup): string {
  return TYPE_GROUP_OPTIONS.find((o) => o.value === group)?.label ?? group;
}

/**
 * Groepeer concepten in een folder per type-key voor de Foldertree-render.
 *
 * Voor archimate-folders: groupKey = concept.typeId (BusinessObject /
 * ApplicationComponent / etc.).
 *
 * Voor sharp/c4/bmc-folders: groupKey = eerste stereotype-id met
 * matching extensie-prefix (sharp.Concept / c4.Container / etc.) zodat
 * "«Concept»" en "«Subject Area»" als aparte sub-groepen verschijnen
 * binnen dezelfde Sharp-folder, ook al delen ze dezelfde base-typeId.
 * Concepten zonder matching stereotype vallen terug op typeId.
 *
 * Labels worden bilingually geresolved via registry (stereotype.label
 * of element-type.label). Groepen worden alfabetisch op label gesorteerd.
 */
function groupConceptsByType(
  concepts: readonly Concept[],
  folderTypeGroup: FolderTypeGroup,
  lang: string,
): Array<{ key: string; label: string; concepts: Concept[] }> {
  const buckets = new Map<string, Concept[]>();
  const groupKey = (c: Concept): string => {
    if (folderTypeGroup !== "archimate") {
      const prefix = `${folderTypeGroup}.`;
      const match = (c.stereotypes ?? []).find((s) => s.startsWith(prefix));
      if (match) return match;
    }
    return c.typeId;
  };
  for (const c of concepts) {
    const k = groupKey(c);
    const arr = buckets.get(k) ?? [];
    arr.push(c);
    buckets.set(k, arr);
  }
  const reg = getRegistry();
  const out = Array.from(buckets.entries()).map(([key, items]) => {
    // Label-lookup: probeer stereotype eerst (voor sharp/c4/bmc-keys),
    // anders element-type, anders raw key.
    const st = reg.getStereotype(key);
    const et = reg.getElementType(key);
    const label = st
      ? resolveBilingual(st.label, lang)
      : et
        ? resolveBilingual(et.label, lang)
        : key;
    return { key, label, concepts: items };
  });
  out.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
  return out;
}

export function FoldertreePanel() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const workspaceId = useSession((s) => s.workspaceId);
  const branchId = useSession((s) => s.branchId);
  const sessionWpId = useSession((s) => s.workpackageId);
  // Alleen kijken: bladeren en openen mag; folders en hun inhoud aanmaken,
  // wijzigen, verplaatsen, exporteren of wissen niet.
  const readOnly = useSession((s) => s.readOnly);
  const classFilter = useUi((s) => s.baseFolderClassFilter);
  const setClassFilter = useUi((s) => s.setBaseFolderClassFilter);
  const selectedConceptId = useUi((s) => s.selectedConceptId);
  const selectConcept = useUi((s) => s.selectConcept);
  const selectRelation = useUi((s) => s.selectRelation);
  const setAttributePanelOpen = useUi((s) => s.setAttributePanelOpen);

  const handleSelectConcept = (conceptId: string, folderId: string, event: React.MouseEvent) => {
    // Shift-klik → range-select tussen anchor en gekliket item, binnen
    // dezelfde folder. Volgorde = de gerenderde flat-volgorde (groups
    // alfabetisch, binnen group de DB-volgorde). Zonder anchor of in
    // andere folder dan anchor → val terug op gewone klik.
    if (event.shiftKey && !event.metaKey && !event.ctrlKey) {
      if (rangeAnchor && rangeAnchor.folderId === folderId) {
        const folderConcepts = conceptsByFolder.get(folderId) ?? [];
        const folder = folders.find((f) => f.id === folderId);
        if (folder) {
          const flat = groupConceptsByType(folderConcepts, folder.typeGroup, lang).flatMap(
            (g) => g.concepts,
          );
          const anchorIdx = flat.findIndex((c) => c.id === rangeAnchor.conceptId);
          const targetIdx = flat.findIndex((c) => c.id === conceptId);
          if (anchorIdx !== -1 && targetIdx !== -1) {
            const [lo, hi] =
              anchorIdx <= targetIdx ? [anchorIdx, targetIdx] : [targetIdx, anchorIdx];
            const ids = new Set(flat.slice(lo, hi + 1).map((c) => c.id));
            setMultiSelectedIds(ids);
            setMultiSelectedFolderId(folderId);
            // Anchor NIET wijzigen: range-shift kan vanuit zelfde anchor
            // worden uitgebreid/ingekort (Finder-conventie).
            return;
          }
        }
      }
      // Geen geldige anchor → behandel als gewone klik (fall-through).
    }
    // Cmd/Ctrl-klik → multi-select toggle binnen één folder.
    if (event.metaKey || event.ctrlKey) {
      if (multiSelectedFolderId !== null && multiSelectedFolderId !== folderId) {
        // Klik in andere folder → reset, start nieuwe selectie hier.
        setMultiSelectedIds(new Set([conceptId]));
        setMultiSelectedFolderId(folderId);
        setRangeAnchor({ conceptId, folderId });
        return;
      }
      setMultiSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(conceptId)) next.delete(conceptId);
        else next.add(conceptId);
        if (next.size === 0) setMultiSelectedFolderId(null);
        else if (multiSelectedFolderId === null) setMultiSelectedFolderId(folderId);
        return next;
      });
      // Anchor zetten op het toegevoegde item zodat een volgende
      // shift-klik vanaf hier rangelt. Bij toggle-uit blijft anchor
      // staan — geen probleem, shift zonder anchor-in-folder fallt door.
      setRangeAnchor({ conceptId, folderId });
      return;
    }
    // Gewone klik → single-select (AttributePanel) + multi-set
    // normaliseren naar **deze ene**. Reden: zodat de multi-set
    // altijd canoniek bevat wat visueel geselecteerd is. Anders kan
    // er een mismatch ontstaan (selectedConceptId = c1, multi = {c2})
    // waarin een drag van c2 alléén c2 zou meenemen terwijl c1 ook
    // blauw oogt. Bij size === 1 verbergen we de "X geselecteerd"-
    // balk hieronder zodat de UI niet vervuilt.
    setMultiSelectedIds(new Set([conceptId]));
    setMultiSelectedFolderId(folderId);
    setRangeAnchor({ conceptId, folderId });
    selectConcept(conceptId);
    setAttributePanelOpen(true);
  };
  const [error, setError] = useState<string | null>(null);
  const [dropTargetId, setDropTargetId] = useState<string | null>(null);
  const [workpackages, setWorkpackages] = useState<WorkPackageDTO[]>([]);

  // M6.5-W3b — WP-selector verhuist tijdelijk naar de Foldertree-header
  // omdat Explorer.tsx (waar 'ie normaal woont) niet rendert in Base.
  // Bij switch naar een andere WP zet useSession.setWorkpackageId
  // localStorage + state; de Editor-layout-switch ziet de wijziging via
  // z'n useEffect en wisselt automatisch naar de Explorer-layout.
  // Re-fetch-counter: WorkpackagesSettings dispatcht
  // `ae:workpackages-changed` na elke succesvolle CRUD; we bumpen
  // de counter zodat de fetch-useEffect opnieuw draait en de
  // selector synchroon blijft met Settings.
  const [wpRefreshCounter, setWpRefreshCounter] = useState(0);
  useEffect(() => {
    const onChanged = () => setWpRefreshCounter((c) => c + 1);
    window.addEventListener("ae:workpackages-changed", onChanged);
    return () => window.removeEventListener("ae:workpackages-changed", onChanged);
  }, []);

  useEffect(() => {
    if (!workspaceId) return;
    let cancelled = false;
    api
      .listWorkpackages(workspaceId)
      .then((r) => {
        if (!cancelled) setWorkpackages(r.workpackages);
      })
      .catch(() => {
        // ignore — selector blijft leeg, error wordt elders gemeld
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceId, wpRefreshCounter]);

  // Base-WP main-branch-id voor folder-concept-queries. Folder-concepten
  // wonen canoniek op Base.main (sinds 35f5fe8 + W6); useSession.branchId
  // kan tijdelijk stale zijn bij WP-switch vanuit non-Base (Explorer
  // unmount voordat 'ie setBranchId aanroept). Daarom resolven we 'm
  // direct uit de workpackages-list zodra die geladen is.
  const baseBranchId = useMemo(() => {
    const baseWp = workpackages.find((wp) => wp.isBase);
    return baseWp?.mainBranchId ?? branchId;
  }, [workpackages, branchId]);

  // WP-dropdown order: Base altijd bovenaan, rest alfabetisch.
  const sortedWorkpackages = useMemo(() => {
    const base = workpackages.filter((wp) => wp.isBase);
    const rest = workpackages
      .filter((wp) => !wp.isBase)
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name));
    return [...base, ...rest];
  }, [workpackages]);

  const handleWpChange = (next: string) => {
    if (!next || next === sessionWpId) return;
    const wp = workpackages.find((w) => w.id === next);
    useSession.getState().setWorkpackageId(next);
    // Forceer ook de branch direct zodat een fetcher die net na deze
    // setState-cycle runt al de juiste branch heeft. Anders kan
    // FoldertreePanel (bij switch naar Base) of Explorer (bij switch
    // naar non-Base) een fetch met stale branchId doen.
    if (wp?.mainBranchId) {
      useSession.getState().setBranchId(wp.mainBranchId);
    }
  };
  const { width: panelWidth, ResizeHandle } = usePanelWidth(
    "ae.panelWidth.foldertree",
    240,
    "right",
  );

  const [folders, setFolders] = useState<FolderDTO[]>([]);
  const [conceptsByFolder, setConceptsByFolder] = useState<Map<string, Concept[]>>(new Map());
  const [relationsByConcept, setRelationsByConcept] = useState<
    Map<string, { outgoing: Relation[]; incoming: Relation[] }>
  >(new Map());
  const [conceptNameById, setConceptNameById] = useState<Map<string, BilingualText>>(new Map());
  const [expandedConcepts, setExpandedConcepts] = useState<Set<string>>(new Set());
  /** Ingeklapte type-groepen per folder. Key = `${folderId}:${groupKey}`.
   *  Default-empty = alle type-groepen uitgeklapt (huidig gedrag); zodra
   *  een gebruiker een groep inklapt komt 'ie in deze set. */
  const [collapsedTypeGroups, setCollapsedTypeGroups] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [editingConceptId, setEditingConceptId] = useState<string | null>(null);
  const [editingConceptName, setEditingConceptName] = useState("");
  const [moveOutTarget, setMoveOutTarget] = useState<{
    folderId: string;
    conceptIds: string[];
  } | null>(null);
  // Multi-select state — cmd/ctrl-klik toggle't in deze set. Folder-id
  // wordt vast gepind op de folder van het eerst-geselecteerde concept
  // zodat alle multi-select-acties dezelfde folder-bron hebben. Bij
  // klik op een concept in een andere folder reset de set.
  const [multiSelectedIds, setMultiSelectedIds] = useState<Set<string>>(new Set());
  const [multiSelectedFolderId, setMultiSelectedFolderId] = useState<string | null>(null);
  // M6.5-W8b — file-input voor folder-import. Eén shared input voor
  // alle folders; bij klik op een folder's 📥-knop wordt
  // `importTargetFolderId` gezet zodat de change-handler weet waar het
  // gekozen bestand naartoe moet.
  const importFileRef = useRef<HTMLInputElement | null>(null);
  const [importTargetFolderId, setImportTargetFolderId] = useState<string | null>(null);

  // Anchor voor shift-klik range-select. Wordt gezet bij elke gewone klik
  // én bij de eerste cmd-klik wanneer er nog geen anchor is — dat is hoe
  // OS-bestandsverkenners het ook doen (anchor = laatste niet-shift-klik).
  const [rangeAnchor, setRangeAnchor] = useState<{
    conceptId: string;
    folderId: string;
  } | null>(null);
  const [query, setQuery] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState<CreateForm>(EMPTY_FORM);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  /** Edit-modal voor naam + beschrijving (de inline-rename via dubbel-
   *  klik kan alleen de naam wijzigen — die snelle weg blijft staan
   *  voor power-users; de modal is voor gebruikers die ook de
   *  beschrijving willen aanpassen of überhaupt willen weten dat 'r
   *  een beschrijving bestaat). */
  const [editFolderModal, setEditFolderModal] = useState<{
    folderId: string;
    name: string;
    description: string;
  } | null>(null);

  /**
   * Synct ook de globale workspace-store na een Foldertree-mutatie.
   * Zonder dit blijft KladblokCanvas (en AttributePanel) een POST/PATCH/
   * DELETE missen tot een hard refresh — die lezen via
   * `useWorkspaceData.store.getConcept()`, niet via de Foldertree's eigen
   * state. Re-hydrate is overhead, maar de Base-WP is klein (folder-
   * objecten only) dus de cost is in praktijk verwaarloosbaar.
   */
  const syncWorkspaceStore = useCallback(() => {
    if (workspaceId && branchId) {
      void useWorkspaceData.getState().hydrate(workspaceId, branchId);
    }
  }, [workspaceId, branchId]);

  const refresh = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      // Gebruik baseBranchId zodat folder-concepten op Base.main wonen
      // ook na een net-uitgevoerde cross-WP move worden opgepikt
      // (useSession.branchId kan stale zijn na WP-switch).
      const queryBranchId = baseBranchId ?? branchId;
      const [foldersRes, conceptsRes, relationsRes] = await Promise.all([
        api.listFolders(workspaceId),
        queryBranchId
          ? api.listConcepts(workspaceId, queryBranchId)
          : Promise.resolve({ items: [] as Concept[], next_cursor: null }),
        queryBranchId
          ? api.listRelations(workspaceId, queryBranchId)
          : Promise.resolve({ items: [] as Relation[], next_cursor: null }),
      ]);
      setFolders(foldersRes.folders);

      // Concept-name-lookup voor relatie-target/source-rendering.
      const nameById = new Map<string, BilingualText>();
      for (const c of conceptsRes.items) {
        nameById.set(c.id, c.name);
      }
      setConceptNameById(nameById);

      const grouped = new Map<string, Concept[]>();
      for (const c of conceptsRes.items) {
        if (!c.folderId) continue;
        const arr = grouped.get(c.folderId) ?? [];
        arr.push(c);
        grouped.set(c.folderId, arr);
      }
      // Sorteer alfabetisch in de actieve UI-taal met fallback naar de
      // andere taal en als laatste de UUID — zodat een EN-only concept
      // bij NL-UI niet random op z'n UUID gaat sorteren.
      const sortLang = lang === "en" ? "en" : "nl";
      const fallbackLang = sortLang === "nl" ? "en" : "nl";
      const sortKey = (c: Concept): string =>
        resolveBilingual(c.name, sortLang) || resolveBilingual(c.name, fallbackLang) || c.id;
      for (const [k, arr] of grouped) {
        arr.sort((a, b) => sortKey(a).localeCompare(sortKey(b), undefined, { numeric: true }));
        grouped.set(k, arr);
      }
      setConceptsByFolder(grouped);

      // Group relations per concept (source = outgoing, target = incoming).
      const relMap = new Map<string, { outgoing: Relation[]; incoming: Relation[] }>();
      const ensure = (id: string) => {
        let bag = relMap.get(id);
        if (!bag) {
          bag = { outgoing: [], incoming: [] };
          relMap.set(id, bag);
        }
        return bag;
      };
      for (const r of relationsRes.items) {
        if (r.tombstoneInBranch) continue;
        ensure(r.sourceConceptId).outgoing.push(r);
        ensure(r.targetConceptId).incoming.push(r);
      }
      setRelationsByConcept(relMap);
      // Sync de globale workspace-store met dezelfde data. Zonder dit
      // mist KladblokCanvas + AttributePanel een net-aangemaakt concept
      // tot een hard refresh — die lezen via useWorkspaceData.store,
      // niet via onze eigen state.
      syncWorkspaceStore();
    } catch (err) {
      const msg = err instanceof ApiError ? (err.body?.detail ?? err.message) : String(err);
      setError(t("foldertree.errors.loadFolders", { msg }));
    } finally {
      setLoading(false);
    }
  }, [workspaceId, branchId, baseBranchId, lang, syncWorkspaceStore, t]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Listen voor cross-component refresh-triggers — JSON/AMEFF-imports
  // dispatchen dit event zodat folders + concepten in Base die zojuist
  // zijn aangemaakt direct zichtbaar worden zonder hard reload.
  useEffect(() => {
    const onChanged = () => void refresh();
    window.addEventListener("ae:workpackages-changed", onChanged);
    return () => window.removeEventListener("ae:workpackages-changed", onChanged);
  }, [refresh]);

  const sorted = useMemo(() => {
    const q = query.trim().toLowerCase();
    let filtered = q ? folders.filter((f) => f.name.toLowerCase().includes(q)) : folders;
    if (classFilter) filtered = filtered.filter((f) => f.typeGroup === classFilter);
    return [...filtered].sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
  }, [folders, query, classFilter]);

  const handleCreate = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!workspaceId) return;
    if (useSession.getState().readOnly) return;
    const name = form.name.trim();
    if (!name) return;
    try {
      await api.createFolder(workspaceId, {
        name,
        description: form.description.trim() || null,
        type_group: form.typeGroup,
      });
      setForm(EMPTY_FORM);
      setCreateOpen(false);
      await refresh();
    } catch (err) {
      const msg = err instanceof ApiError ? (err.body?.detail ?? err.message) : String(err);
      setError(t("foldertree.errors.createFolder", { msg }));
    }
  };

  const beginRename = (f: FolderDTO) => {
    if (useSession.getState().readOnly) return;
    setEditingId(f.id);
    setEditingName(f.name);
  };

  /** Opent de edit-modal met naam + beschrijving van een bestaande folder. */
  const openEditFolderModal = (f: FolderDTO) => {
    setEditFolderModal({
      folderId: f.id,
      name: f.name,
      description: f.description ?? "",
    });
  };

  /** Schrijft naam + beschrijving naar de server. Lege beschrijving
   *  wordt als `null` doorgegeven zodat de DB-kolom NULL wordt
   *  i.p.v. een lege string. */
  const commitEditFolderModal = async () => {
    if (!workspaceId || !editFolderModal) return;
    const name = editFolderModal.name.trim();
    if (!name) return;
    const description = editFolderModal.description.trim();
    try {
      await api.patchFolder(workspaceId, editFolderModal.folderId, {
        name,
        description: description.length > 0 ? description : null,
      });
      setEditFolderModal(null);
      await refresh();
    } catch (err) {
      const msg = err instanceof ApiError ? (err.body?.detail ?? err.message) : String(err);
      setError(t("foldertree.errors.updateFolder", { msg }));
    }
  };

  const commitRename = async () => {
    if (!workspaceId || !editingId) return;
    if (useSession.getState().readOnly) return;
    const next = editingName.trim();
    if (!next) {
      setEditingId(null);
      return;
    }
    try {
      await api.patchFolder(workspaceId, editingId, { name: next });
      setEditingId(null);
      await refresh();
    } catch (err) {
      const msg = err instanceof ApiError ? (err.body?.detail ?? err.message) : String(err);
      setError(t("foldertree.errors.renameFolder", { msg }));
    }
  };

  const toggleFolderExpansion = (folderId: string) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) next.delete(folderId);
      else next.add(folderId);
      return next;
    });
  };

  const toggleConceptExpansion = (conceptId: string) => {
    setExpandedConcepts((prev) => {
      const next = new Set(prev);
      if (next.has(conceptId)) next.delete(conceptId);
      else next.add(conceptId);
      return next;
    });
  };

  const toggleTypeGroupCollapsed = (folderId: string, groupKey: string) => {
    const k = `${folderId}:${groupKey}`;
    setCollapsedTypeGroups((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };

  const handleRelationDelete = async (r: Relation) => {
    if (!workspaceId || !branchId) return;
    if (useSession.getState().readOnly) return;
    const ok = window.confirm(t("foldertree.relation.deleteConfirm", { typeId: r.typeId }));
    if (!ok) return;
    try {
      await api.deleteRelation(workspaceId, branchId, r.id);
      await refresh();
    } catch (err) {
      const msg = err instanceof ApiError ? (err.body?.detail ?? err.message) : String(err);
      setError(t("foldertree.errors.deleteRelation", { msg }));
    }
  };

  const beginConceptRename = (c: Concept, lang: string) => {
    if (useSession.getState().readOnly) return;
    setEditingConceptId(c.id);
    setEditingConceptName(resolveBilingual(c.name, lang) || c.id);
  };

  const commitConceptRename = async (lang: string) => {
    if (!workspaceId || !branchId || !editingConceptId) return;
    const next = editingConceptName.trim();
    if (!next) {
      setEditingConceptId(null);
      return;
    }
    const current = [...conceptsByFolder.values()].flat().find((c) => c.id === editingConceptId);
    if (!current) {
      setEditingConceptId(null);
      return;
    }
    const newName: BilingualText = { ...current.name, [lang]: next };
    try {
      await api.patchConcept(workspaceId, branchId, editingConceptId, { name: newName });
      setEditingConceptId(null);
      await refresh();
    } catch (err) {
      const msg = err instanceof ApiError ? (err.body?.detail ?? err.message) : String(err);
      setError(t("foldertree.errors.renameConcept", { msg }));
    }
  };

  const handleConceptDelete = async (c: Concept, lang: string) => {
    if (!workspaceId || !branchId) return;
    if (useSession.getState().readOnly) return;
    const label = resolveBilingual(c.name, lang) || c.id;
    const ok = window.confirm(t("foldertree.concept.deleteConfirm", { label }));
    if (!ok) return;
    try {
      await api.deleteConcept(workspaceId, branchId, c.id);
      await refresh();
    } catch (err) {
      const msg = err instanceof ApiError ? (err.body?.detail ?? err.message) : String(err);
      setError(t("foldertree.errors.deleteConcept", { msg }));
    }
  };

  const handleDrop = async (ev: React.DragEvent, folder: FolderDTO) => {
    ev.preventDefault();
    setDropTargetId(null);
    if (!workspaceId || !branchId) return;
    if (useSession.getState().readOnly) return;

    // Intra-Base move-tussen-folders heeft voorrang op palette-drag wanneer
    // beide MIMEs aanwezig zijn — bij intra-Base bestaat het concept al, we
    // verhuizen folder_id i.p.v. een nieuw concept aan te maken.
    const moveRaw = ev.dataTransfer.getData(FOLDER_MOVE_MIME);
    if (moveRaw) {
      let payload: { conceptIds?: unknown; sourceFolderId?: unknown; folderClass?: unknown };
      try {
        payload = JSON.parse(moveRaw);
      } catch {
        return;
      }
      const conceptIds = Array.isArray(payload.conceptIds)
        ? payload.conceptIds.filter((v): v is string => typeof v === "string")
        : [];
      const sourceFolderId =
        typeof payload.sourceFolderId === "string" ? payload.sourceFolderId : null;
      const moveFolderClass = typeof payload.folderClass === "string" ? payload.folderClass : null;
      if (conceptIds.length === 0 || !sourceFolderId) return;
      if (sourceFolderId === folder.id) return; // no-op binnen zelfde folder
      if (moveFolderClass && moveFolderClass !== folder.typeGroup) {
        setError(
          t("foldertree.errors.selectionClassMismatch", {
            cls: moveFolderClass,
            folder: folder.name,
            group: folder.typeGroup,
          }),
        );
        return;
      }
      try {
        const res = await api.moveConceptsToFolder(workspaceId, folder.id, conceptIds);
        if (res.skipped.length > 0) {
          setError(
            t("moveToFolder.partial", {
              moved: res.moved.length,
              skipped: res.skipped.length,
              reason: res.skipped[0]?.reason ?? t("moveToFolder.unknown"),
            }),
          );
        }
        setExpandedFolders((prev) => {
          if (prev.has(folder.id)) return prev;
          const next = new Set(prev);
          next.add(folder.id);
          return next;
        });
        // Multi-select state resetten — de selectie zit nu in een andere folder.
        setMultiSelectedIds(new Set());
        setMultiSelectedFolderId(null);
        await refresh();
      } catch (err) {
        const msg = err instanceof ApiError ? (err.body?.detail ?? err.message) : String(err);
        setError(t("foldertree.errors.moveToFolder", { folder: folder.name, msg }));
      }
      return;
    }

    // Drag van Explorer of canvas (alleen `application/ae-existing-
    // concept-id`, geen FOLDER_MOVE_MIME): zelfde move-naar-folder-flow,
    // maar zonder source-folder-info. Server detecteert zelf de huidige
    // folder en verplaatst. type_group-mismatch wordt bij de PATCH
    // gevalideerd (folder_type_group_mismatch).
    const existingConceptId = ev.dataTransfer.getData("application/ae-existing-concept-id");
    if (existingConceptId && !ev.dataTransfer.getData(TYPE_ID_MIME)) {
      try {
        const res = await api.moveConceptsToFolder(workspaceId, folder.id, [existingConceptId]);
        if (res.skipped.length > 0) {
          setError(
            t("foldertree.errors.moveSkipped", {
              reason: res.skipped[0]?.reason ?? t("foldertree.errors.unknownReason"),
            }),
          );
        }
        setExpandedFolders((prev) => {
          if (prev.has(folder.id)) return prev;
          const next = new Set(prev);
          next.add(folder.id);
          return next;
        });
        await refresh();
      } catch (err) {
        const msg = err instanceof ApiError ? (err.body?.detail ?? err.message) : String(err);
        setError(t("foldertree.errors.moveToFolder", { folder: folder.name, msg }));
      }
      return;
    }

    const typeId = ev.dataTransfer.getData(TYPE_ID_MIME);
    if (!typeId) return;
    const folderClass = ev.dataTransfer.getData(FOLDER_CLASS_MIME) || "archimate";
    if (folderClass !== folder.typeGroup) {
      setError(
        t("foldertree.errors.paletteClassMismatch", {
          typeId,
          folderClass,
          folder: folder.name,
          group: folder.typeGroup,
        }),
      );
      return;
    }
    const name = readDroppedTypeName(ev.dataTransfer) ?? { nl: typeId, en: typeId };
    const stereoRaw = ev.dataTransfer.getData(DEFAULT_STEREOTYPES_MIME);
    const attrRaw = ev.dataTransfer.getData(DEFAULT_ATTRIBUTES_MIME);
    let stereotypes: string[] = [];
    if (stereoRaw) {
      try {
        const parsed = JSON.parse(stereoRaw);
        if (Array.isArray(parsed)) stereotypes = parsed.filter((s) => typeof s === "string");
      } catch {
        // ignore malformed
      }
    }
    let attributes: Record<string, unknown> = {};
    if (attrRaw) {
      try {
        const parsed = JSON.parse(attrRaw);
        if (parsed && typeof parsed === "object") attributes = parsed as Record<string, unknown>;
      } catch {
        // ignore malformed
      }
    }
    try {
      await api.createConcept(workspaceId, branchId, {
        type_id: typeId,
        name,
        attributes,
        stereotypes,
        folder_id: folder.id,
      });
      // Open de doelfolder automatisch zodat het nieuwe concept zichtbaar
      // wordt — anders moet de gebruiker eerst zelf op de chevron klikken.
      setExpandedFolders((prev) => {
        if (prev.has(folder.id)) return prev;
        const next = new Set(prev);
        next.add(folder.id);
        return next;
      });
      await refresh();
    } catch (err) {
      const msg = err instanceof ApiError ? (err.body?.detail ?? err.message) : String(err);
      setError(t("foldertree.errors.createConcept", { folder: folder.name, msg }));
    }
  };

  const handleFolderExport = async (f: FolderDTO) => {
    if (!workspaceId) return;
    if (useSession.getState().readOnly) return;
    try {
      const { blob, filename } = await api.exportFolder(workspaceId, f.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      const msg = err instanceof ApiError ? (err.body?.detail ?? err.message) : String(err);
      setError(t("foldertree.errors.export", { msg }));
    }
  };

  const handleFolderImportPick = (f: FolderDTO) => {
    setImportTargetFolderId(f.id);
    importFileRef.current?.click();
  };

  const handleFolderImportFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = ""; // reset
    if (!file || !workspaceId || !importTargetFolderId) return;
    const targetFolder = folders.find((x) => x.id === importTargetFolderId);
    if (!targetFolder) return;
    if (
      !window.confirm(
        t("foldertree.import.confirm", { file: file.name, folder: targetFolder.name }),
      )
    ) {
      setImportTargetFolderId(null);
      return;
    }
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const res = await api.importFolder(workspaceId, importTargetFolderId, parsed);
      const skippedNote =
        res.relations_skipped.length > 0
          ? t("foldertree.import.skippedNote", { n: res.relations_skipped.length })
          : "";
      const updatedNote = res.concepts_updated
        ? t("foldertree.import.updatedNote", { n: res.concepts_updated })
        : "";
      const reassignedNote = res.ids_reassigned
        ? t("foldertree.import.reassignedNote", { n: res.ids_reassigned })
        : "";
      if (res.error) {
        setError(res.error);
        return;
      }
      setError(null);
      // Re-use de error-strook voor positieve info — eenvoudiger dan een
      // tweede toast. Niet echt 'n error, maar de UI heeft geen aparte
      // info-channel hier.
      window.alert(
        t("foldertree.import.success", {
          inserted: res.concepts_inserted,
          updatedNote,
          relations: res.relations_inserted,
          skippedNote,
          reassignedNote,
        }),
      );
      setExpandedFolders((prev) => {
        const next = new Set(prev);
        next.add(importTargetFolderId);
        return next;
      });
      await refresh();
    } catch (err) {
      const msg = err instanceof ApiError ? (err.body?.detail ?? err.message) : String(err);
      setError(t("foldertree.errors.import", { msg }));
    } finally {
      setImportTargetFolderId(null);
    }
  };

  const handleDelete = async (f: FolderDTO) => {
    if (!workspaceId) return;
    if (useSession.getState().readOnly) return;
    const ok = window.confirm(t("foldertree.folder.deleteConfirm", { name: f.name }));
    if (!ok) return;
    try {
      await api.deleteFolder(workspaceId, f.id);
      await refresh();
    } catch (err) {
      const msg = err instanceof ApiError ? (err.body?.detail ?? err.message) : String(err);
      setError(t("foldertree.errors.deleteFolder", { msg }));
    }
  };

  /** Folder-scoped snapshot. Triggert POST /snapshots-workpackage/workpackage
   *  met folder_id=X zodat de backend `resolveScope` → kind="folder" pakt en
   *  alleen de concepten + relaties binnen die folder vastlegt (geen diagrams
   *  — folders zijn concept-clusters, geen view-bezitters). Restore zet
   *  alleen díé folder terug, andere folders ongemoeid.
   */
  const handleFolderSnapshot = async (f: FolderDTO) => {
    if (!workspaceId || !branchId) return;
    try {
      const res = await api.createWorkpackageSnapshot(workspaceId, branchId, {
        folder_id: f.id,
      });
      const counts = res.snapshot.counts;
      window.alert(
        t("foldertree.folder.snapshotSuccess", {
          name: f.name,
          concepts: counts.concepts,
          relations: counts.relations,
        }),
      );
    } catch (err) {
      const msg = err instanceof ApiError ? (err.body?.detail ?? err.message) : String(err);
      setError(t("foldertree.errors.snapshot", { msg }));
    }
  };

  return (
    <aside
      data-scope="explorer"
      className="ledger relative flex h-full min-h-0 min-w-0 shrink-0 flex-col overflow-x-hidden border-(--ld-control) border-r bg-(--ld-surface)"
      style={{ width: panelWidth }}
    >
      {/* M6.5-W8b — hidden file-input voor folder-import. */}
      <input
        ref={importFileRef}
        type="file"
        accept="application/json,.json"
        onChange={(e) => void handleFolderImportFile(e)}
        className="hidden"
      />
      <div className="border-(--ld-border) border-b bg-(--ld-raised) px-2 py-2">
        {/* Label boven de WP-selector weggehaald — de selector zelf
            spreekt voor zich en winst horizontale + verticale ruimte.
            min-w-0 zodat lange WP-namen worden afgekapt i.p.v. de
            hele aside breder duwen. */}
        <select
          value={sessionWpId ?? ""}
          onChange={(e) => handleWpChange(e.target.value)}
          className="w-full min-w-0 rounded border border-(--ld-control) px-2 py-1 text-sm"
          title={t("foldertree.wpSelectTitle")}
        >
          {workpackages.length === 0 && <option value="">{t("settings.common.loading")}</option>}
          {sortedWorkpackages.map((wp) => (
            <option key={wp.id} value={wp.id}>
              {wp.name}
              {wp.isBase ? t("foldertree.baseSuffix") : ""}
            </option>
          ))}
        </select>
      </div>
      <header className="flex items-center justify-between gap-2 border-(--ld-border) border-b px-2 py-2">
        <h2 className="font-semibold text-(--ld-text) text-sm">{t("foldertree.title")}</h2>
        <button
          type="button"
          onClick={() => setCreateOpen((v) => !v)}
          hidden={readOnly}
          className="rounded border border-(--ld-control) px-2 py-0.5 text-(--ld-text) text-xs hover:bg-(--ld-raised)"
          aria-expanded={createOpen}
        >
          {createOpen ? t("settings.common.cancel") : t("foldertree.newFolder")}
        </button>
      </header>

      {createOpen && (
        <form
          onSubmit={handleCreate}
          className="flex flex-col gap-2 border-(--ld-border) border-b bg-(--ld-raised) px-3 py-2"
        >
          <label className="flex flex-col gap-1 text-xs">
            <span className="text-(--ld-muted)">{t("foldertree.form.name")}</span>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              // biome-ignore lint/a11y/noAutofocus: form opens on explicit button-click, focus is expected
              autoFocus
              required
              className="rounded border border-(--ld-control) px-2 py-1 text-sm"
              placeholder={t("foldertree.form.namePlaceholder")}
            />
          </label>
          <label className="flex flex-col gap-1 text-xs">
            <span className="text-(--ld-muted)">{t("foldertree.form.description")}</span>
            <input
              type="text"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="rounded border border-(--ld-control) px-2 py-1 text-sm"
            />
          </label>
          <fieldset className="flex flex-col gap-1 text-xs">
            <span className="text-(--ld-muted)">{t("foldertree.form.typeClass")}</span>
            <div className="flex flex-wrap gap-2">
              {TYPE_GROUP_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  className={`flex cursor-pointer items-center gap-1 rounded border px-2 py-1 text-xs ${
                    form.typeGroup === opt.value
                      ? "border-(--ld-accent) bg-(--ld-surface)"
                      : "border-(--ld-control) bg-(--ld-surface)/60"
                  }`}
                >
                  <input
                    type="radio"
                    name="typeGroup"
                    value={opt.value}
                    checked={form.typeGroup === opt.value}
                    onChange={() => setForm({ ...form, typeGroup: opt.value })}
                    className="sr-only"
                  />
                  <span
                    aria-hidden
                    className="inline-block h-2 w-2 rounded-full"
                    style={{ backgroundColor: opt.color }}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </fieldset>
          <div className="flex justify-end gap-2 pt-1">
            <button
              type="submit"
              className="rounded bg-(--ld-primary) px-3 py-1 text-(--ld-primary-ink) text-xs hover:bg-(--ld-primary-hover)"
            >
              {t("foldertree.form.submit")}
            </button>
          </div>
        </form>
      )}

      {error && (
        <div className="border-(--ld-control) border-b bg-(--ld-negative-bg) px-3 py-2 text-(--ld-negative) text-xs">
          {error}
          <button
            type="button"
            onClick={() => setError(null)}
            className="ml-2 underline"
            aria-label={t("foldertree.errorBar.closeAria")}
          >
            {t("foldertree.errorBar.close")}
          </button>
        </div>
      )}

      <div className="flex flex-col gap-2 border-(--ld-border) border-b px-2 py-2">
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("foldertree.filter.searchPlaceholder")}
          className="w-full rounded border border-(--ld-control) px-2 py-1 text-sm"
        />
        <label className="flex items-center gap-2 text-(--ld-muted) text-xs">
          <span>{t("foldertree.filter.label")}</span>
          <select
            value={classFilter ?? ""}
            onChange={(e) =>
              setClassFilter(e.target.value === "" ? null : (e.target.value as FolderTypeGroup))
            }
            className="flex-1 rounded border border-(--ld-control) px-2 py-1 text-sm"
          >
            <option value="">{t("foldertree.filter.all")}</option>
            {TYPE_GROUP_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {multiSelectedIds.size >= 1 && multiSelectedFolderId && (
        <div className="flex items-center gap-2 border-(--ld-border) border-b bg-(--ld-active) px-3 py-1.5 text-xs">
          <span className="text-(--ld-accent)">
            {t("foldertree.multiSelect.selectedIn", {
              count: multiSelectedIds.size,
              folder: folders.find((f) => f.id === multiSelectedFolderId)?.name ?? "?",
            })}
          </span>
          <button
            type="button"
            onClick={() => {
              if (!multiSelectedFolderId) return;
              setMoveOutTarget({
                folderId: multiSelectedFolderId,
                conceptIds: Array.from(multiSelectedIds),
              });
            }}
            hidden={readOnly}
            title={t("foldertree.multiSelect.moveOutTitle")}
            aria-label={t("foldertree.multiSelect.moveOutTitle")}
            className="ml-auto flex items-center justify-center rounded border border-(--ld-accent) bg-(--ld-surface) px-1.5 py-0.5 text-(--ld-accent) hover:bg-(--ld-active)"
          >
            <span className="text-base leading-none">📂</span>
          </button>
          <button
            type="button"
            hidden={readOnly}
            onClick={async () => {
              if (!workspaceId || !branchId) return;
              if (useSession.getState().readOnly) return;
              const ids = Array.from(multiSelectedIds);
              if (ids.length === 0) return;
              if (
                !window.confirm(t("foldertree.multiSelect.deleteConfirm", { count: ids.length }))
              ) {
                return;
              }
              let succeeded = 0;
              // Eén gedeelde action-id voor de bulk-delete zodat de
              // Prullenbak de groep als 1 entry toont met +/▸-expand.
              const actionId = crypto.randomUUID();
              for (const id of ids) {
                try {
                  await api.deleteConcept(workspaceId, branchId, id, undefined, actionId);
                  succeeded += 1;
                } catch (err) {
                  const msg = err instanceof ApiError ? err.message : String(err);
                  console.error("[foldertree] bulk-delete failed for", id, msg);
                }
              }
              setMultiSelectedIds(new Set());
              setMultiSelectedFolderId(null);
              await refresh();
              syncWorkspaceStore();
              if (succeeded < ids.length) {
                setError(
                  t("foldertree.multiSelect.deletePartial", {
                    ok: succeeded,
                    total: ids.length,
                    failed: ids.length - succeeded,
                  }),
                );
              }
            }}
            title={t("foldertree.multiSelect.deleteTitle")}
            aria-label={t("foldertree.multiSelect.deleteTitle")}
            className="flex items-center justify-center rounded border border-(--ld-control) bg-(--ld-surface) px-1.5 py-0.5 text-(--ld-negative) hover:bg-(--ld-negative-bg)"
          >
            <span className="text-base leading-none">🗑</span>
          </button>
          <button
            type="button"
            onClick={() => {
              setMultiSelectedIds(new Set());
              setMultiSelectedFolderId(null);
            }}
            className="rounded px-1 text-(--ld-muted) hover:text-(--ld-text)"
            title={t("foldertree.multiSelect.clearTitle")}
          >
            ×
          </button>
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto px-1 py-2">
        <div className="mb-2 flex items-center gap-1 px-1 text-(--ld-text) text-sm">
          <span aria-hidden className="text-(--ld-muted)">
            ▾
          </span>
          <strong>{t("foldertree.baseRoot")}</strong>
          <span className="text-(--ld-muted) text-xs">({sorted.length})</span>
        </div>
        {loading ? (
          <p className="px-2 text-(--ld-muted) text-xs italic">{t("foldertree.loadingFolders")}</p>
        ) : sorted.length === 0 ? (
          <p className="px-2 text-(--ld-muted) text-xs italic">{t("foldertree.noFolders")}</p>
        ) : (
          <ul className="ml-1 flex flex-col gap-0.5 border-(--ld-border) border-l pl-1">
            {sorted.map((f) => {
              const expanded = expandedFolders.has(f.id);
              const folderConcepts = conceptsByFolder.get(f.id) ?? [];
              return (
                <li key={f.id} className="flex flex-col">
                  {/* biome-ignore lint/a11y/noStaticElementInteractions: folder-rij; interactief (klik + drop-target) maar nog niet keyboard-focusbaar — treeitem-roving-a11y volgt in de tree-a11y-ronde */}
                  <div
                    className={`group flex items-center gap-1 rounded px-1 py-0.5 text-sm ${
                      dropTargetId === f.id ? "bg-(--ld-notice-bg) ring-1 ring-(--ld-accent)" : ""
                    }`}
                    onDragOver={(ev) => {
                      const isPalette = ev.dataTransfer.types.includes(TYPE_ID_MIME);
                      const isMove = ev.dataTransfer.types.includes(FOLDER_MOVE_MIME);
                      if (isPalette || isMove) {
                        ev.preventDefault();
                        ev.dataTransfer.dropEffect = isMove ? "move" : "copy";
                        if (dropTargetId !== f.id) setDropTargetId(f.id);
                      }
                    }}
                    onDragLeave={() => {
                      if (dropTargetId === f.id) setDropTargetId(null);
                    }}
                    onDrop={(ev) => void handleDrop(ev, f)}
                  >
                    <button
                      type="button"
                      onClick={() => toggleFolderExpansion(f.id)}
                      className="inline-block w-5 shrink-0 text-(--ld-muted) text-base leading-none hover:text-(--ld-text)"
                      aria-expanded={expanded}
                      aria-label={
                        expanded
                          ? t("foldertree.folder.collapseAria")
                          : t("foldertree.folder.expandAria")
                      }
                    >
                      {expanded ? "▾" : "▸"}
                    </button>
                    <span
                      aria-hidden
                      title={t("foldertree.folder.typeClassTitle", { group: f.typeGroup })}
                      className="inline-block h-2 w-2 shrink-0 rounded-full"
                      style={{ backgroundColor: badgeColor(f.typeGroup) }}
                    />
                    {editingId === f.id ? (
                      <input
                        type="text"
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        onBlur={commitRename}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") void commitRename();
                          if (e.key === "Escape") setEditingId(null);
                        }}
                        // biome-ignore lint/a11y/noAutofocus: inline-rename triggered by explicit user click, focus is the whole point
                        autoFocus
                        className="flex-1 rounded border border-(--ld-control) px-1 py-0 text-sm"
                      />
                    ) : (
                      <button
                        type="button"
                        onDoubleClick={() => beginRename(f)}
                        className="flex-1 cursor-text truncate text-left text-(--ld-text) hover:text-(--ld-text)"
                        title={f.description ?? f.name}
                      >
                        {f.name}{" "}
                        <span className="text-(--ld-muted) text-xs">
                          ({typeGroupLabel(f.typeGroup)}
                          {folderConcepts.length > 0
                            ? t("foldertree.folder.objCount", { n: folderConcepts.length })
                            : ""}
                          )
                        </span>
                      </button>
                    )}
                    {/* Hidden i.p.v. opacity-0: bij opacity-0 reserveerde
                        deze div nog steeds ~140px (5 buttons + gaps) flex-
                        ruimte, waardoor de folder-naam-button ver te vroeg
                        truncate'de ondanks beschikbare panel-breedte. */}
                    <div
                      className="hidden items-center gap-1 group-hover:flex focus-within:flex"
                      hidden={readOnly}
                    >
                      <button
                        type="button"
                        onClick={() => openEditFolderModal(f)}
                        title={t("foldertree.folder.editTitle")}
                        className="rounded px-1 text-(--ld-muted) hover:bg-(--ld-raised) hover:text-(--ld-text)"
                      >
                        ✎
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleFolderExport(f)}
                        title={t("foldertree.folder.exportTitle")}
                        className="rounded px-1 font-bold text-(--ld-negative) hover:bg-(--ld-negative-bg)"
                        aria-label={t("foldertree.folder.exportAria")}
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        onClick={() => handleFolderImportPick(f)}
                        title={t("foldertree.folder.importTitle")}
                        className="rounded px-1 font-bold text-(--ld-positive) hover:bg-(--ld-positive-bg)"
                        aria-label={t("foldertree.folder.importAria")}
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleFolderSnapshot(f)}
                        title={t("foldertree.folder.snapshotTitle")}
                        className="rounded px-1 text-(--ld-muted) hover:bg-(--ld-active) hover:text-(--ld-accent)"
                        aria-label={t("foldertree.folder.snapshotAria")}
                      >
                        📸
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(f)}
                        title={t("settings.common.delete")}
                        className="rounded px-1 text-(--ld-muted) hover:bg-(--ld-negative-bg) hover:text-(--ld-negative)"
                      >
                        🗑
                      </button>
                    </div>
                  </div>
                  {expanded && (
                    <ul className="ml-2 flex flex-col gap-0.5 border-(--ld-border) border-l pl-1">
                      {folderConcepts.length === 0 ? (
                        <li className="px-1 py-0.5 text-(--ld-muted) text-xs italic">
                          {t("foldertree.folder.empty")}
                        </li>
                      ) : (
                        groupConceptsByType(folderConcepts, f.typeGroup, lang).map((grp) => {
                          const groupCollapsed = collapsedTypeGroups.has(`${f.id}:${grp.key}`);
                          return (
                            <li key={`grp:${grp.key}`} className="flex flex-col">
                              <button
                                type="button"
                                onClick={() => toggleTypeGroupCollapsed(f.id, grp.key)}
                                className="flex items-center gap-1 px-1 py-0.5 font-medium text-(--ld-muted) text-xs hover:text-(--ld-text)"
                                aria-expanded={!groupCollapsed}
                                aria-label={
                                  groupCollapsed
                                    ? t("foldertree.folder.expandTypeGroupAria")
                                    : t("foldertree.folder.collapseTypeGroupAria")
                                }
                              >
                                <span
                                  aria-hidden
                                  className="inline-block w-3 text-center text-(--ld-muted)"
                                >
                                  {groupCollapsed ? "▸" : "▾"}
                                </span>
                                <span>
                                  {grp.label}{" "}
                                  <span className="text-(--ld-muted)">({grp.concepts.length})</span>
                                </span>
                              </button>
                              {!groupCollapsed && (
                                <ul className="ml-1 flex flex-col gap-0.5">
                                  {grp.concepts.map((c) => {
                                    const rels = relationsByConcept.get(c.id) ?? {
                                      outgoing: [],
                                      incoming: [],
                                    };
                                    return (
                                      <ConceptRow
                                        key={c.id}
                                        concept={c}
                                        lang={lang}
                                        isEditing={editingConceptId === c.id}
                                        editValue={editingConceptName}
                                        isSelected={
                                          selectedConceptId === c.id || multiSelectedIds.has(c.id)
                                        }
                                        isExpanded={expandedConcepts.has(c.id)}
                                        outgoing={rels.outgoing}
                                        incoming={rels.incoming}
                                        conceptNameById={conceptNameById}
                                        onToggleExpansion={() => toggleConceptExpansion(c.id)}
                                        onSelect={(e) => handleSelectConcept(c.id, f.id, e)}
                                        onOpenAttributePanel={() => {
                                          selectConcept(c.id);
                                          setAttributePanelOpen(true);
                                        }}
                                        onBeginRename={() => beginConceptRename(c, lang)}
                                        onChangeEditValue={setEditingConceptName}
                                        onCommitRename={() => void commitConceptRename(lang)}
                                        onCancelRename={() => setEditingConceptId(null)}
                                        onDelete={() => void handleConceptDelete(c, lang)}
                                        onDeleteRelation={(r) => void handleRelationDelete(r)}
                                        onOpenRelationAttributePanel={(r) => {
                                          selectRelation(r.id);
                                          setAttributePanelOpen(true);
                                        }}
                                        onMoveOut={() =>
                                          setMoveOutTarget({ folderId: f.id, conceptIds: [c.id] })
                                        }
                                        folderId={f.id}
                                        folderClass={f.typeGroup}
                                        dragConceptIds={
                                          multiSelectedFolderId === f.id &&
                                          multiSelectedIds.has(c.id)
                                            ? Array.from(multiSelectedIds)
                                            : [c.id]
                                        }
                                      />
                                    );
                                  })}
                                </ul>
                              )}
                            </li>
                          );
                        })
                      )}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <footer className="border-(--ld-border) border-t bg-(--ld-raised) px-3 py-2 text-(--ld-muted) text-xs italic">
        {t("foldertree.footer")}
      </footer>

      <ResizeHandle />
      {moveOutTarget && workspaceId && (
        <MoveOutOfFolderDialog
          workspaceId={workspaceId}
          folderId={moveOutTarget.folderId}
          conceptIds={moveOutTarget.conceptIds}
          onClose={(didMove) => {
            setMoveOutTarget(null);
            if (didMove) {
              setMultiSelectedIds(new Set());
              setMultiSelectedFolderId(null);
              void refresh();
            }
          }}
        />
      )}
      {editFolderModal && (
        // biome-ignore lint/a11y/noStaticElementInteractions: presentationele backdrop; klik-buiten is muis-gemak, Escape + sluitknop zijn het toegankelijke pad
        <div
          className="ledger fixed inset-0 z-[1000] flex items-center justify-center bg-slate-900/40 p-4"
          onClick={() => setEditFolderModal(null)}
          role="presentation"
        >
          <div
            className="w-[480px] max-w-[90vw] rounded-md border border-(--ld-control) bg-(--ld-surface) p-4 shadow-xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <h2 className="mb-3 font-semibold text-(--ld-text) text-sm">
              {t("foldertree.editModal.title")}
            </h2>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void commitEditFolderModal();
              }}
              className="flex flex-col gap-3"
            >
              <label className="flex flex-col gap-1 text-xs">
                <span className="text-(--ld-muted)">{t("foldertree.form.name")}</span>
                <input
                  type="text"
                  value={editFolderModal.name}
                  onChange={(e) => setEditFolderModal({ ...editFolderModal, name: e.target.value })}
                  // biome-ignore lint/a11y/noAutofocus: modal opens on explicit click — focus is expected
                  autoFocus
                  required
                  className="rounded border border-(--ld-control) px-2 py-1 text-sm"
                />
              </label>
              <label className="flex flex-col gap-1 text-xs">
                <span className="text-(--ld-muted)">{t("foldertree.form.description")}</span>
                <textarea
                  value={editFolderModal.description}
                  onChange={(e) =>
                    setEditFolderModal({ ...editFolderModal, description: e.target.value })
                  }
                  rows={4}
                  className="rounded border border-(--ld-control) px-2 py-1 text-sm"
                  placeholder={t("foldertree.form.descriptionPlaceholder")}
                />
              </label>
              <div className="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setEditFolderModal(null)}
                  className="rounded border border-(--ld-control) px-3 py-1 text-(--ld-text) text-xs hover:bg-(--ld-raised)"
                >
                  {t("settings.common.cancel")}
                </button>
                <button
                  type="submit"
                  className="rounded bg-(--ld-primary) px-3 py-1 text-(--ld-primary-ink) text-xs hover:bg-(--ld-primary-hover)"
                >
                  {t("settings.common.save")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </aside>
  );
}
